import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const industries = [
  { emoji: '🚗', icon: 'ri-steering-line', name: 'Automobile', description: 'Connecting engineering talent with India\'s fastest-growing auto sector.' },
  { emoji: '🛒', icon: 'ri-shopping-bag-3-line', name: 'E-Commerce', description: 'Building digital-first teams for the modern commerce ecosystem.' },
  { emoji: '📚', icon: 'ri-book-open-line', name: 'Education', description: 'Placing educators, administrators and leadership in top institutions.' },
  { emoji: '⚙️', icon: 'ri-settings-5-line', name: 'Manufacturing', description: 'Plant leadership, operations and process engineering talent.' },
  { emoji: '🏦', icon: 'ri-bank-line', name: 'Banking & Insurance', description: 'Specialist hiring for finance, risk, compliance and insurance roles.' },
  { emoji: '📺', icon: 'ri-film-line', name: 'Media & Advertising', description: 'Creative, strategy and production roles for media organisations.' },
  { emoji: '🎧', icon: 'ri-headphone-line', name: 'BPO & KPO', description: 'High-volume and specialised process hiring at scale.' },
  { emoji: '🌐', icon: 'ri-global-line', name: 'And More', description: 'Our network spans sectors — if you need talent, we can find it.' },
];

const colorPalette = [
  { bg: 'bg-emerald-100', iconColor: 'text-emerald-600', dot: 'bg-emerald-500' },
  { bg: 'bg-sky-100', iconColor: 'text-sky-600', dot: 'bg-sky-500' },
  { bg: 'bg-cyan-100', iconColor: 'text-cyan-600', dot: 'bg-cyan-500' },
  { bg: 'bg-amber-100', iconColor: 'text-amber-600', dot: 'bg-amber-500' },
  { bg: 'bg-indigo-100', iconColor: 'text-indigo-600', dot: 'bg-indigo-500' },
  { bg: 'bg-rose-100', iconColor: 'text-rose-600', dot: 'bg-rose-500' },
  { bg: 'bg-violet-100', iconColor: 'text-violet-600', dot: 'bg-violet-500' },
  { bg: 'bg-primary-100', iconColor: 'text-primary-700', dot: 'bg-primary-600' },
];

export default function IndustriesSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal(0.05);

  return (
    <section id="industries" className="relative bg-background-50 py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div
          ref={headerRef}
          className={`text-center mb-16 md:mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-600 text-xs font-bold tracking-widest uppercase mb-5">
            Industries We Serve
          </span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-[1.1] max-w-3xl mx-auto">
            <span style={{ color: '#f0a0b5' }}>Deep Expertise</span>{' '}
            <span className="text-foreground-950 italic">Across Every Sector</span>
          </h2>
          <p className="mt-5 text-foreground-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            We don&apos;t just place people — we understand the industries they work in, deeply.
          </p>
        </div>

        <div
          ref={gridRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
        >
          {industries.map((industry, idx) => {
            const palette = colorPalette[idx];
            const isLast = idx === industries.length - 1;
            return (
              <div
                key={industry.name}
                className={`group rounded-2xl p-6 md:p-7 transition-all duration-500 cursor-pointer ${
                  isLast
                    ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                    : 'bg-background-100 hover:bg-background-200 border border-background-200 hover:border-foreground-200'
                } ${
                  gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: gridVisible ? `${idx * 80}ms` : '0ms' }}
              >
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-5 transition-all duration-400 relative ${
                  isLast
                    ? 'bg-background-50/15'
                    : palette.bg
                }`}>
                  <span className="text-2xl">{industry.emoji}</span>
                </div>

                <h3 className={`font-heading text-xl md:text-2xl font-bold mb-2 ${
                  isLast ? 'text-background-50' : 'text-foreground-900'
                }`}>
                  {industry.name}
                </h3>
                <p className={`text-sm leading-relaxed ${
                  isLast ? 'text-background-50/80' : 'text-foreground-500'
                }`}>
                  {industry.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}